import React, { useCallback, useState } from 'react'

import { keyBy } from 'lodash/fp'

import {
  Typography,
  TypographyVariant,
  TypographyWeight,
} from 'src/components/atoms'

import { getIdValue, removeById } from './helpers'
import { TBody } from './tbody'
import { THead } from './thead'
import { Check, Data, ITableProps } from './types'
import styles from './styles.module.css'

const Table = ({
  columns,
  data,
  idPropertyName,
  onRowSelect,
  onRowClick,
  emptyState,
}: ITableProps): JSX.Element => {
  const [selectedRows, setSelectedRows] = useState<
    Record<string | number, unknown>
  >({})

  const getIdOfRow = useCallback(
    (row: Data) => getIdValue(idPropertyName, row),
    [idPropertyName],
  )

  const onRowSelectWithReturn = useCallback(
    (list: Data) => {
      if (onRowSelect) {
        onRowSelect(list)
      }
      return list
    },
    [onRowSelect],
  )

  const toggleRowSelect = useCallback(
    (row: Data): void => {
      const id = getIdOfRow(row)

      const getAddOrRemoveElementFromList = (current: Data): Data => {
        if (current[id]) {
          return onRowSelectWithReturn(removeById(current, id))
        }
        return onRowSelectWithReturn({ ...current, [id]: row })
      }

      setSelectedRows(getAddOrRemoveElementFromList)
    },
    [getIdOfRow, onRowSelectWithReturn],
  )

  const onSelectAll = useCallback(
    (type: Check) => {
      if (type === Check.unchecked) {
        setSelectedRows(onRowSelectWithReturn({}))
        return
      }
      setSelectedRows(onRowSelectWithReturn(keyBy(idPropertyName, data)))
    },
    [data, idPropertyName, onRowSelectWithReturn],
  )

  return (
    <table className={styles.table}>
      <THead columns={columns} onSelectAll={onSelectAll} />
      {data.length === 0 && emptyState ? (
        <tbody>
          <tr>
            <td colSpan={columns.length} className="!border-b-0 p-0">
              <div
                className="flex w-full flex-col items-center justify-center gap-2 px-4 py-16 md:py-20"
                role="status"
                aria-live="polite"
              >
                <Typography
                  variant={TypographyVariant.title2Xs}
                  weight={TypographyWeight.light}
                  className="text-center text-content-primary"
                >
                  {emptyState.title}
                </Typography>
                <Typography
                  variant={TypographyVariant.bodyXs}
                  weight={TypographyWeight.regular}
                  className="max-w-md text-center text-content-secondary"
                >
                  {emptyState.description}
                </Typography>
              </div>
            </td>
          </tr>
        </tbody>
      ) : (
        <TBody
          columns={columns}
          data={data}
          onSelectRow={toggleRowSelect}
          selectedRows={selectedRows}
          getIdOfRow={getIdOfRow}
          onRowClick={onRowClick}
        />
      )}
    </table>
  )
}

export type { ITableEmptyState, ITableProps } from './types'
export { Table }
export { THead } from './thead'
